import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import React, { createContext, useMemo, useContext, useState, useRef, useEffect, createElement, forwardRef } from 'react';
import { Ninetailed, selectVariant, makeExperienceSelectMiddleware, selectHasExperienceVariants, selectExperience, selectExperienceVariant } from '@ninetailed/experience.js';
import { logger, isBrowser } from '@ninetailed/experience.js-shared';
import { isEqual, get } from 'radash';
import { useInView } from 'react-intersection-observer';
import { isForwardRef } from 'react-is';

const NinetailedContext = /*#__PURE__*/createContext(undefined);

const NinetailedProvider = props => {
  const ninetailed = useMemo(() => {
    if ('ninetailed' in props) {
      return props.ninetailed;
    }
    const {
      clientId,
      environment,
      preview,
      url,
      locale,
      requestTimeout,
      plugins = [],
      onLog,
      onError,
      buildClientContext,
      onInitProfileId,
      componentViewTrackingThreshold,
      storageImpl
    } = props;
    return new Ninetailed({
      clientId,
      environment,
      preview
    }, {
      url,
      plugins,
      locale,
      requestTimeout,
      onLog,
      onError,
      buildClientContext,
      onInitProfileId,
      componentViewTrackingThreshold,
      storageImpl
    });
  }, []);
  const {
    children
  } = props;
  return jsx(NinetailedContext.Provider, Object.assign({
    value: ninetailed
  }, {
    children: children
  }));
};

const useNinetailed = () => {
  const ninetailed = useContext(NinetailedContext);
  if (ninetailed === undefined) {
    throw new Error('The component using the the context must be a descendant of the NinetailedProvider');
  }
  return ninetailed;
};

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

const useProfile = () => {
  const ninetailed = useNinetailed();
  const [profileState, setProfileState] = useState(ninetailed.profileState);
  const profileStateRef = useRef({});
  /**
   * This effect compares the old and new profile state before updating it.
   * We use a ref to avoid an infinite loop which can happen when an empty profile state was updated with no changes.
   * This behaviour occurred as the validation handling on the error property was not set properly in the "CreateProfile" and "UpdateProfile" endpoint types.
   * Furthermore, it was also observed, that it "only" occurred when the preview plugin was used in parallel.
   */
  useEffect(() => {
    ninetailed.onProfileChange(profileState => {
      if (isEqual(profileState, profileStateRef.current)) {
        logger.debug('Profile State Did Not Change', profileState);
        return;
      } else {
        setProfileState(profileState);
        profileStateRef.current = profileState;
        logger.debug('Profile State Changed', profileState);
      }
    });
  }, []);
  const profileStateWithoutExperiences = __rest(profileState, ["experiences"]);
  return Object.assign(Object.assign({}, profileStateWithoutExperiences), {
    loading: profileState.status === 'loading'
  });
};

const usePersonalize = (baseline, variants, options = {
  holdout: -1
}) => {
  const profile = useProfile();
  return selectVariant(baseline, variants, profile, options);
};

const TrackHasSeenComponent = ({
  children,
  variant,
  audience,
  isPersonalized
}) => {
  const ninetailed = useNinetailed();
  const {
    ref,
    inView
  } = useInView({
    triggerOnce: true
  });
  useEffect(() => {
    if (isBrowser() && inView) {
      ninetailed.trackHasSeenComponent({
        variant,
        audience,
        isPersonalized
      });
    }
  }, [inView]);
  return jsxs(Fragment, {
    children: [jsx("div", {
      ref: ref
    }), children]
  });
};

const Personalize = _a => {
  var {
      component: Component,
      loadingComponent: LoadingComponent,
      variants = [],
      holdout = -1
    } = _a,
    baseline = __rest(_a, ["component", "loadingComponent", "variants", "holdout"]);
  const {
    loading,
    variant,
    isPersonalized,
    audience
  } = usePersonalize(baseline, variants, {
    holdout
  });
  const hasVariants = variants.length > 0;
  if (!hasVariants) {
    return jsx(Component, Object.assign({}, baseline, {
      ninetailed: {
        isPersonalized,
        audience
      }
    }));
  }
  if (loading) {
    if (LoadingComponent) {
      return jsx(LoadingComponent, {});
    }
    return jsx("div", Object.assign({
      style: {
        opacity: 0
      }
    }, {
      children: jsx(Component, Object.assign({}, variant, {
        ninetailed: {
          isPersonalized,
          audience
        }
      }))
    }), "hide");
  }
  return jsx(TrackHasSeenComponent, Object.assign({
    variant: variant,
    audience: audience,
    isPersonalized: isPersonalized
  }, {
    children: /*#__PURE__*/createElement(Component, Object.assign({}, variant, {
      key: `${audience.id}-${variant.id}`,
      ninetailed: {
        isPersonalized,
        audience
      }
    }))
  }));
};

const generateSelectors = id => {
  return id.split('_').map((path, index, paths) => {
    const dotPath = paths.slice(0, index).join('.');
    const underScorePath = paths.slice(index).join('_');
    return [dotPath, underScorePath].filter(path => path !== '').join('.');
  });
};
const selectValueFromProfile = (profile, id) => {
  const selectors = generateSelectors(id);
  const matchingSelector = selectors.find(selector => get(profile, selector));
  if (!matchingSelector) {
    return null;
  }
  return get(profile, matchingSelector);
};

const MergeTag = ({
  id,
  fallback
}) => {
  const {
    loading,
    profile
  } = useProfile();
  if (loading || !profile) {
    return null;
  }
  const value = selectValueFromProfile(profile, id) || fallback;
  return value ? jsx(Fragment, {
    children: value
  }) : null;
};

const useExperienceSelectionMiddleware = ({
  experiences,
  baseline,
  profile
}) => {
  const {
    plugins
  } = useNinetailed();
  const [_, setCurrentTime] = useState(Date.now());
  const {
    addListeners,
    removeListeners,
    middleware
  } = useMemo(() => makeExperienceSelectMiddleware({
    plugins,
    experiences,
    baseline,
    profile,
    onChange: () => setCurrentTime(Date.now())
  }), [plugins, experiences, baseline, profile]);
  useEffect(() => {
    addListeners();
    return () => {
      removeListeners();
    };
  }, [addListeners, removeListeners]);
  return middleware;
};

const useExperience = ({
  baseline,
  experiences
}) => {
  const profileState = useProfile();
  const hasVariants = experiences.map(experience => selectHasExperienceVariants(experience, baseline)).reduce((acc, curr) => acc || curr, false);
  const {
    status,
    profile
  } = profileState;
  const experienceSelectionMiddleware = useExperienceSelectionMiddleware({
    experiences,
    baseline,
    profile
  });
  const overrideResult = _a => {
    var {
        experience: originalExperience,
        variant: originalVariant,
        variantIndex: originalVariantIndex
      } = _a,
      other = __rest(_a, ["experience", "variant", "variantIndex"]);
    const {
      experience,
      variant,
      variantIndex
    } = experienceSelectionMiddleware({
      experience: originalExperience,
      variant: originalVariant,
      variantIndex: originalVariantIndex
    });
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return Object.assign(Object.assign({}, other), {
      audience: (experience === null || experience === void 0 ? void 0 : experience.audience) ? experience.audience : null,
      experience,
      variant,
      variantIndex
    });
  };
  const baseReturn = Object.assign(Object.assign({}, profileState), {
    hasVariants,
    baseline
  });
  const emptyReturn = Object.assign(Object.assign({}, baseReturn), {
    experience: null,
    variant: baseline,
    variantIndex: 0,
    audience: null,
    isPersonalized: false,
    profile: null
  });
  if (status === 'loading') {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return overrideResult(emptyReturn);
  }
  if (status === 'error') {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return overrideResult(emptyReturn);
  }
  if (!profile) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return overrideResult(emptyReturn);
  }
  const experience = selectExperience({
    experiences,
    profile
  });
  if (!experience) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return overrideResult(Object.assign(Object.assign({}, emptyReturn), {
      profile
    }));
  }
  const {
    variant,
    index
  } = selectExperienceVariant({
    baseline,
    experience,
    profile
  });
  return overrideResult(Object.assign(Object.assign({}, baseReturn), {
    status: 'success',
    loading: false,
    error: null,
    experience,
    variant,
    variantIndex: index,
    audience: experience.audience ? experience.audience : null,
    profile,
    isPersonalized: true
  }));
};

const ComponentMarker = /*#__PURE__*/forwardRef((_, ref) => {
  const {
    logger
  } = useNinetailed();
  const markerRef = useRef(null);
  useEffect(() => {
    var _a;
    /*
    Due to React's limitation on setting !important styles during rendering, we set the display property on the DOM element directly.
    See: https://github.com/facebook/react/issues/1881
    */
    (_a = markerRef.current) === null || _a === void 0 ? void 0 : _a.style.setProperty('display', 'none', 'important');
  }, []);
  useEffect(() => {
    logger.debug('Using fallback mechanism to detect when experiences are seen. This can lead to inaccurate results. Consider using a forwardRef instead. See: https://docs.ninetailed.io/for-developers/experience-sdk/rendering-experiences#tracking-impressions-of-experiences');
  }, [logger]);
  useEffect(() => {
    if (markerRef.current) {
      const nextSibling = markerRef.current.nextSibling;
      if (ref) {
        if (typeof ref === 'function') {
          ref(nextSibling);
        } else {
          ref.current = nextSibling;
        }
      }
    }
  }, []);
  return jsx("div", {
    className: "nt-cmp-marker",
    style: {
      display: 'none'
    },
    ref: markerRef
  });
});

const DefaultExperienceLoadingComponent = _a => {
  var {
      component: Component,
      unhideAfterMs = 5000,
      passthroughProps
    } = _a,
    baseline = __rest(_a, ["component", "unhideAfterMs", "passthroughProps"]);
  const {
    logger
  } = useNinetailed();
  const [hidden, setHidden] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => {
      setHidden(false);
      logger.error(new Error(`The experience was still in loading state after ${unhideAfterMs}ms. That happens when no events are sent to the Ninetailed API. The baseline is now shown instead.`));
    }, unhideAfterMs);
    return () => {
      clearTimeout(timer);
    };
  }, []);
  if (hidden) {
    return jsx("div", Object.assign({
      style: {
        opacity: 0
      }
    }, {
      children: jsx(Component, Object.assign({}, passthroughProps, baseline, {
        ninetailed: {
          isPersonalized: false,
          audience: {
            id: 'baseline'
          }
        }
      }))
    }), "hide");
  }
  return jsx(Component, Object.assign({}, passthroughProps, baseline, {
    ninetailed: {
      isPersonalized: false,
      audience: {
        id: 'baseline'
      }
    }
  }));
};
const Experience = _a => {
  var {
      experiences,
      component: Component,
      loadingComponent: LoadingComponent = DefaultExperienceLoadingComponent,
      passthroughProps
    } = _a,
    baseline = __rest(_a, ["experiences", "component", "loadingComponent", "passthroughProps"]);
  const {
    observeElement,
    unobserveElement,
    logger
  } = useNinetailed();
  const {
    status,
    hasVariants,
    experience,
    variant,
    variantIndex,
    audience,
    isPersonalized
  } = useExperience({
    baseline,
    experiences
  });
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const isComponentForwardRef = isForwardRef(jsx(Component, {}));
  const componentRef = useRef(null);
  useEffect(() => {
    const componentElement = componentRef.current;
    if (componentElement && !(componentElement instanceof Element)) {
      const isObject = typeof componentElement === 'object' && componentElement !== null;
      const constructorName = isObject ? componentElement.constructor.name : '';
      const isConstructorNameNotObject = constructorName && constructorName !== 'Object';
      logger.warn(`The component ref being in Experience is an invalid element. Expected an Element but got ${typeof componentElement}${isConstructorNameNotObject ? ` of type ${constructorName}` : ''}. This component won't be observed.`);
      return () => {
        // noop
      };
    }
    if (componentElement) {
      observeElement({
        element: componentElement,
        experience,
        audience,
        variant,
        variantIndex
      });
      return () => {
        if (componentElement) {
          unobserveElement(componentElement);
        }
      };
    }
    return () => {
      // noop
    };
  }, [observeElement, unobserveElement, experience, baseline, variant, variantIndex, audience]);
  if (!hasVariants) {
    return jsxs(Fragment, {
      children: [!isComponentForwardRef && jsx(ComponentMarker, {
        ref: componentRef
      }, `marker-no-variants-${(experience === null || experience === void 0 ? void 0 : experience.id) || 'baseline'}-${variant.id}`), /*#__PURE__*/createElement(Component, Object.assign({}, passthroughProps, baseline, {
        key: baseline.id
      }, isComponentForwardRef ? {
        ref: componentRef
      } : {}))]
    });
  }
  if (status === 'loading') {
    return /*#__PURE__*/createElement(LoadingComponent, Object.assign({}, baseline, {
      key: baseline.id,
      passthroughProps: passthroughProps,
      experiences: experiences,
      component: Component
    }));
  }
  const isVariantHidden = 'hidden' in variant && variant.hidden;
  if (isVariantHidden) {
    return jsx(ComponentMarker, {
      ref: componentRef
    }, `marker-hidden-${(experience === null || experience === void 0 ? void 0 : experience.id) || 'baseline'}-${variant.id}`);
  }
  return jsxs(Fragment, {
    children: [!isComponentForwardRef && jsx(ComponentMarker, {
      ref: componentRef
    }, `marker-${(experience === null || experience === void 0 ? void 0 : experience.id) || 'baseline'}-${variant.id}`), /*#__PURE__*/createElement(Component, Object.assign({}, Object.assign(Object.assign({}, passthroughProps), variant), {
      key: `${(experience === null || experience === void 0 ? void 0 : experience.id) || 'baseline'}-${variant.id}`,
      ninetailed: {
        isPersonalized,
        audience: {
          id: (audience === null || audience === void 0 ? void 0 : audience.id) || 'all visitors'
        }
      }
    }, isComponentForwardRef ? {
      ref: componentRef
    } : {}))]
  });
};

const ESRContext = /*#__PURE__*/React.createContext(undefined);
const ESRProvider = ({
  experienceVariantsMap,
  children
}) => {
  return jsx(ESRContext.Provider, Object.assign({
    value: {
      experienceVariantsMap
    }
  }, {
    children: children
  }));
};
const useESR = () => {
  const context = React.useContext(ESRContext);
  if (context === undefined) {
    throw new Error('The component using the the context must be a descendant of the ESRProvider');
  }
  return {
    experienceVariantsMap: context.experienceVariantsMap
  };
};
const ESRLoadingComponent = _a => {
  var {
      experiences,
      component: Component,
      passthroughProps
    } = _a,
    baseline = __rest(_a, ["experiences", "component", "passthroughProps"]);
  const {
    experienceVariantsMap
  } = useESR();
  const experience = experiences.find(experience => Object.prototype.hasOwnProperty.call(experienceVariantsMap, experience.id));
  if (!experience) {
    return jsx(Component, Object.assign({}, passthroughProps, baseline, {
      ninetailed: {
        isPersonalized: false,
        audience: {
          id: 'baseline'
        }
      }
    }));
  }
  const component = experience.components.find(component => component.baseline.id === baseline.id);
  if (!component) {
    return jsx(Component, Object.assign({}, passthroughProps, baseline, {
      ninetailed: {
        isPersonalized: false,
        audience: {
          id: 'baseline'
        }
      }
    }));
  }
  if (experienceVariantsMap[experience.id] === 0) {
    return jsx(Component, Object.assign({}, passthroughProps, baseline, {
      ninetailed: {
        isPersonalized: false,
        audience: {
          id: 'baseline'
        }
      }
    }));
  }
  const variant = component.variants[experienceVariantsMap[experience.id] - 1];
  if (!variant) {
    return jsx(Component, Object.assign({}, passthroughProps, baseline, {
      ninetailed: {
        isPersonalized: false,
        audience: {
          id: 'baseline'
        }
      }
    }));
  }
  return jsx(Component, Object.assign({}, passthroughProps, variant, {
    ninetailed: {
      isPersonalized: false,
      audience: {
        id: 'baseline'
      }
    }
  }));
};

export { DefaultExperienceLoadingComponent, ESRLoadingComponent, ESRProvider, Experience, MergeTag, NinetailedProvider, Personalize, TrackHasSeenComponent, useExperience, useNinetailed, usePersonalize, useProfile };
